import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { collectionData } from "rxfire/firestore";
import { map } from "rxjs";
import { EvaluationTest } from "types/EvaluationTest";
import { Paragraphs } from "types/Paragraph";

export const useReportedProblems = () => {
  const streamReportedProblems = ({
    status,
    checklistCriteriaName,
  }: {
    status: string;
    checklistCriteriaName?: string;
  }) => {
    const db = useFirestore();

    const collectionRef = collection(db, "/evaluationTests");
    let collectionQuery = query(collectionRef, where("status", "==", status));

    if (checklistCriteriaName != undefined) {
      collectionQuery = query(
        collectionQuery,
        where(
          "originalReportedProblem.checklistCriteriaName",
          "==",
          checklistCriteriaName
        ),
        orderBy("createdAtTimestamp", "desc")
      );
    } else {
      collectionQuery = query(
        collectionQuery,
        orderBy("createdAtTimestamp", "desc"),
        limit(50)
      );
    }

    return collectionData(collectionQuery, { idField: "id" }).pipe(
      map((docs) => {
        return docs.map((doc) => {
          return doc as EvaluationTest;
        });
      })
    );
  };

  const fetchEvaluationTest = async (id: string) => {
    const db = useFirestore();
    const docRef = doc(db, `/evaluationTests/${id}`);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return {
        ...docSnap.data(),
        id: docSnap.id,
      } as EvaluationTest;
    } else {
      throw new Error("EvaluationTest not found");
    }
  };

  const updateEvaluationTest = async (
    evaluationTest: EvaluationTest,
    basePath?: string
  ) => {
    const db = useFirestore();
    const batch = writeBatch(db);

    const docRef = doc(
      db,
      `${basePath ?? ""}/evaluationTests/${evaluationTest.id}`
    );
    batch.update(docRef, evaluationTest);

    if (evaluationTest.jiraIdentifiers) {
      for (var jiraIdentifier of evaluationTest.jiraIdentifiers) {
        const jiraTicketRef = doc(db, `/jiraTickets/${jiraIdentifier}`);
        batch.set(
          jiraTicketRef,
          { identifier: jiraIdentifier },
          { merge: true }
        );
      }
    }

    await batch.commit();
  };

  const deleteEvaluationTest = async (evaluationTest: EvaluationTest) => {
    if (!evaluationTest.id) return;

    const db = useFirestore();
    const docRef = doc(db, `/evaluationTests/${evaluationTest.id}`);
    await deleteDoc(docRef);
  };

  const copyDocument = async (
    evaluationTestId: string,
    documentId: string,
    revisionId: string,
    paragraphs: Paragraphs
  ) => {
    // get all paragraphs for the test and delete them.
    // set all the paragraphs

    const db = useFirestore();
    const documentRef = doc(db, `/testDocuments/${evaluationTestId}`);
    const revisionsRef = doc(documentRef, `revisions/${revisionId}`);
    const paragraphsRef = collection(revisionsRef, "paragraphs");

    const originaDocumentPath = doc(db, `/documents/${documentId}`);
    const originalRevisionsPath = doc(
      originaDocumentPath,
      `revisions/${revisionId}`
    );

    const originalDocumentSnapshot = await getDoc(originaDocumentPath);
    const originalRevisionSnapshot = await getDoc(originalRevisionsPath);

    const batch = writeBatch(db);

    // Copy the original Document Snapshot to the document and revision path

    if (originalDocumentSnapshot.data()) {
      batch.set(documentRef, originalDocumentSnapshot.data()!);
    }

    if (originalRevisionSnapshot.data()) {
      batch.set(revisionsRef, originalRevisionSnapshot.data()!);
    }

    const paragraphsSnapshot = await getDocs(paragraphsRef);

    for (const doc of paragraphsSnapshot.docs) {
      batch.delete(doc.ref);
    }

    for (const paragraph of paragraphs) {
      const paragraphRef = doc(paragraphsRef, paragraph.id);
      batch.set(paragraphRef, paragraph);
    }

    await batch.commit();
  };

  return {
    streamReportedProblems,
    updateEvaluationTest,
    fetchEvaluationTest,
    deleteEvaluationTest,
    copyDocument,
  };
};
